import { Controller } from "@hotwired/stimulus";
import { post } from "@rails/request.js";

// Drives the saved-cards list on the Nexus payment surface: set default and remove. The endpoints
// respond with the re-rendered list partial, which replaces the list wholesale, so per-row state
// (like an open remove confirmation) resets naturally on every successful action.
export default class extends Controller {
  static targets = ["list", "error", "addCardToggle"];
  static values = { setDefaultUrl: String, removeUrl: String };

  // Reveals the add-card flow: the toggle disappears and everything marked collapsible — the
  // customer fields grid, the card element section, and the submit button, which live at
  // different levels of the form — becomes visible.
  showAddCard() {
    this.addCardToggleTarget.hidden = true;
    this.element
      .closest("form")
      ?.querySelectorAll("[data-nexus-add-card-collapsible]")
      .forEach((section) => {
        section.removeAttribute("hidden");
        section.classList.remove("!hidden");
      });
  }

  setDefault(event) {
    this.submit(this.setDefaultUrlValue, event.params.id);
  }

  remove(event) {
    this.submit(this.removeUrlValue, event.params.id);
  }

  confirmRemove(event) {
    this.toggleConfirm(event.target, true);
  }

  cancelRemove(event) {
    this.toggleConfirm(event.target, false);
  }

  async submit(url, paymentMethodId) {
    this.clearError();
    this.setBusy(true);

    try {
      const response = await post(url, {
        body: JSON.stringify(this.withoutBlanks({
          payment_method_id: paymentMethodId,
          ticket_id: this.formValue("ticket_id"),
          short_link_code: this.formValue("short_link_code"),
          session_id: this.formValue("session_id"),
          embedded_in_customer_portal: this.formValue("embedded_in_customer_portal"),
        })),
        contentType: "application/json",
        responseKind: "json",
      });

      const body = await response.json.catch(() => ({}));
      if (body.html) this.listTarget.innerHTML = body.html;
      if (!response.ok) this.showError(body.error);
    } catch {
      this.showError();
    } finally {
      this.setBusy(false);
    }
  }

  toggleConfirm(button, confirming) {
    const row = button.closest("[data-payment-method-id]");
    if (!row) return;

    row.querySelector("[data-role='actions']").hidden = confirming;
    row.querySelector("[data-role='confirm']").hidden = !confirming;
  }

  setBusy(busy) {
    this.listTarget.setAttribute("aria-busy", busy);
    this.listTarget.querySelectorAll("button").forEach((button) => (button.disabled = busy));
    this.listTarget.classList.toggle("opacity-50", busy);
  }

  showError(message) {
    this.errorTarget.textContent = message || "Something went wrong. Please try again.";
    this.errorTarget.hidden = false;
  }

  clearError() {
    this.errorTarget.textContent = "";
    this.errorTarget.hidden = true;
  }

  // formValue returns "" for fields the current form doesn't render. Sending those keys makes an
  // absent identifier look present to the server, so drop them rather than posting empty strings.
  withoutBlanks(body) {
    return Object.fromEntries(Object.entries(body).filter(([, value]) => value !== ""));
  }

  formValue(name) {
    return this.element.closest("form")?.querySelector(`[name="${name}"]`)?.value || "";
  }
}
